// Product page (PDP) behaviour:
//   - Gallery: thumb click smoothly scrolls main pager to that slide;
//     active thumb syncs as user swipes (IntersectionObserver)
//   - Variant picker: option change → finds matching variant, updates
//     hidden variant_id, price block, and CTA availability
//   - Quantity selector: +/- buttons increment/decrement the input
//   - Add to Cart / Buy Now: same loading cycle as product-card via the
//     shared window.toyCart utility (assets/cart-actions.js)
//
// Recommendations ("You might also like") are loaded by the shared
// assets/recommendations.js, included from the recommendations section.

const BUYING_LABEL = 'Loading…';

// ===== Gallery: thumbs sync with the scroll-snap main pager =====
function initGallery(page) {
  const main = page.querySelector('[data-gallery-main]');
  const thumbs = page.querySelectorAll('[data-thumb-index]');
  if (!main || thumbs.length === 0) return;

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', (event) => {
      event.preventDefault();
      const index = parseInt(thumb.dataset.thumbIndex, 10);
      const slide = main.children[index];
      if (slide) {
        main.scrollTo({ left: slide.offsetLeft - main.offsetLeft, behavior: 'smooth' });
      }
    });
  });

  // Sync active thumb as user swipes the main pager.
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio < 0.5) return;
          const slideIndex = Array.from(main.children).indexOf(entry.target);
          thumbs.forEach((t, i) => {
            t.classList.toggle('pdp__thumb--active', i === slideIndex);
          });
        });
      },
      { root: main, threshold: 0.5 },
    );
    Array.from(main.children).forEach((slide) => observer.observe(slide));
  }
}

// ===== Variant picker: option change → variant id + price + availability =====
function initVariantPicker(page) {
  const picker = page.querySelector('[data-variant-picker]');
  if (!picker) return;

  const variantsScript = picker.querySelector('[data-variants]');
  if (!variantsScript) return;

  let variants;
  try {
    variants = JSON.parse(variantsScript.textContent);
  } catch (error) {
    console.error('[product-page] variant JSON parse failed:', error);
    return;
  }

  // The hidden input now lives in the form (not the picker) so single-
  // variant products still work without the variant_picker block.
  const variantIdInput = page.querySelector('[data-product-form] [data-variant-id]');
  const radios = picker.querySelectorAll('.variant-picker__radio');
  const formatMoney = window.toyCart ? window.toyCart.formatMoney : (c) => `${c / 100}`;

  function readSelectedOptions() {
    const options = [];
    radios.forEach((r) => {
      if (r.checked) options[parseInt(r.dataset.optionIndex, 10) - 1] = r.value;
    });
    return options;
  }

  function findMatchingVariant(selected) {
    return variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
  }

  function updateSelectedLabel(input) {
    const fieldset = input.closest('.variant-picker__group');
    const label = fieldset && fieldset.querySelector('.variant-picker__selected');
    if (label) label.textContent = input.value;
  }

  function updatePrice(variant) {
    const priceBlock = page.querySelector('[data-price-block]');
    if (!priceBlock) return;
    const display = priceBlock.querySelector('[data-price-display]');
    const compare = priceBlock.querySelector('[data-price-compare]');
    const discount = priceBlock.querySelector('[data-price-discount]');

    // Storewide sale display: recompute the shown price for the new variant so
    // the discount persists across variant switches. The original variant price
    // becomes the strike-through "was".
    const salePct = parseInt(priceBlock.dataset.salePercent || '0', 10);
    let shownPrice = variant.price;
    let compareValue = variant.compare_at_price;
    let onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    let pct = onSale
      ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
      : 0;

    if (salePct > 0) {
      shownPrice = Math.round((variant.price * (100 - salePct)) / 100);
      compareValue = variant.price;
      onSale = true;
      pct = salePct;
    }

    if (display) {
      display.innerHTML = `<span class="money money--display pdp__price-main">${formatMoney(shownPrice)}</span>`;
    }

    const stickyPrice = page.querySelector('[data-sticky-price]');
    if (stickyPrice) {
      stickyPrice.innerHTML = `<span class="money money--body">${formatMoney(shownPrice)}</span>`;
    }

    if (onSale) {
      if (compare) {
        compare.hidden = false;
        compare.innerHTML = `<span class="money money--strike pdp__price-compare">${formatMoney(compareValue)}</span>`;
      }
      if (discount) {
        discount.hidden = false;
        discount.textContent = `−${pct}%`;
      }
    } else {
      if (compare) { compare.hidden = true; compare.innerHTML = ''; }
      if (discount) { discount.hidden = true; discount.textContent = ''; }
    }
  }

  function updateLowStock(variant) {
    const el = page.querySelector('[data-low-stock]');
    if (!el) return;
    const threshold = parseInt(el.dataset.lowStockThreshold || '0', 10);
    const textEl = el.querySelector('[data-low-stock-text]');
    const qty = variant.inventory_quantity;
    const tracked = Boolean(variant.inventory_management);
    const oversell = variant.inventory_policy === 'continue';
    if (tracked && !oversell && variant.available && qty > 0 && qty <= threshold) {
      const template = el.dataset.lowStockTemplate || 'Only %count% left!';
      if (textEl) textEl.textContent = template.replace('%count%', qty);
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  function updateQuantityMax(variant) {
    const input = page.querySelector('[data-quantity-input]');
    if (!input) return;
    const tracked = Boolean(variant.inventory_management);
    const capped = tracked && variant.inventory_policy !== 'continue';
    const max = capped ? Math.max(1, variant.inventory_quantity) : 99;
    input.max = String(max);
    if ((parseInt(input.value, 10) || 1) > max) {
      input.value = String(max);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function updateCTAAvailability(variant) {
    const addBtn = page.querySelector('[data-add-to-cart]');
    const buyBtn = page.querySelector('[data-buy-now]');
    const stickyBtn = page.querySelector('[data-sticky-add]');
    const available = variant && variant.available;

    [addBtn, buyBtn, stickyBtn].forEach((btn) => {
      if (!btn) return;
      btn.disabled = !available;
      const label = btn.querySelector('[data-cta-label]');
      if (!label) return;
      if (!available) label.textContent = 'Sold out';
      else if (btn.hasAttribute('data-buy-now')) label.textContent = 'Buy Now';
      else label.textContent = 'Add to cart';
    });
  }

  radios.forEach((radio) => {
    radio.addEventListener('change', (event) => {
      updateSelectedLabel(event.target);
      const selected = readSelectedOptions();
      const variant = findMatchingVariant(selected);
      if (!variant) return;
      if (variantIdInput) variantIdInput.value = variant.id;
      updatePrice(variant);
      updateCTAAvailability(variant);
      updateLowStock(variant);
      updateQuantityMax(variant);
    });
  });
}

// ===== Quantity selector =====
function initQuantitySelector(page) {
  page.querySelectorAll('[data-quantity-selector]').forEach((wrap) => {
    const input = wrap.querySelector('[data-quantity-input]');
    if (!input) return;

    const min = parseInt(input.min || '1', 10);

    // Read max each time — it changes per variant (updateQuantityMax).
    const clamp = () => {
      const max = parseInt(input.max || '99', 10);
      let v = parseInt(input.value, 10);
      if (Number.isNaN(v)) v = min;
      v = Math.min(Math.max(v, min), max);
      if (String(v) !== input.value) input.value = String(v);
    };

    wrap.querySelectorAll('[data-quantity-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const max = parseInt(input.max || '99', 10);
        const current = parseInt(input.value, 10) || min;
        const next = btn.dataset.quantityAction === 'increment'
          ? Math.min(current + 1, max)
          : Math.max(current - 1, min);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    // Clamp manual typing to [min, max] so a shopper can't enter more than stock.
    input.addEventListener('change', clamp);
    input.addEventListener('blur', clamp);
  });
}

// ===== Add to Cart / Buy Now =====
function initPurchaseButtons(page) {
  if (!window.toyCart) return;
  const form = page.querySelector('[data-product-form]');
  if (!form) return;

  const addBtn = form.querySelector('[data-add-to-cart]');
  const buyBtn = form.querySelector('[data-buy-now]');

  // Add to Cart — form submit
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!addBtn) return;
    await window.toyCart.performAdd(addBtn, () => new FormData(form));
  });

  // Buy Now — express checkout for THIS product only: empty the cart, add the
  // selected variant, then go straight to checkout (so any earlier cart items
  // aren't pulled into this purchase).
  if (buyBtn) {
    buyBtn.addEventListener('click', async () => {
      if (buyBtn.disabled) return;
      window.toyCart.setBusy(buyBtn, BUYING_LABEL);
      try {
        await window.toyCart.clearCart();
        await window.toyCart.addToCart(new FormData(form));
        window.location.href = '/checkout';
      } catch (error) {
        window.toyCart.setError(buyBtn, error.message);
      }
    });
  }
}

// ===== Sticky add-to-cart bar (mobile) =====
// Shows once the main CTA scrolls out of view; the button reuses the main
// product form + the shared add flow so the selected variant/quantity apply.
function initStickyBar(page) {
  const bar = page.querySelector('[data-sticky-bar]');
  if (!bar) return;

  const anchor =
    page.querySelector('[data-add-to-cart]') || page.querySelector('[data-price-block]');
  if (anchor && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          bar.classList.toggle('pdp__sticky--visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 },
    );
    observer.observe(anchor);
  }

  const stickyBtn = bar.querySelector('[data-sticky-add]');
  const form = page.querySelector('[data-product-form]');
  if (stickyBtn && form && window.toyCart) {
    stickyBtn.addEventListener('click', () => {
      if (stickyBtn.disabled) return;
      window.toyCart.performAdd(stickyBtn, () => new FormData(form));
    });
  }
}

// Collapsible product description ("See more"). Progressive enhancement: without
// JS the full description shows and the toggle stays hidden. Mirrors the
// collection description pattern in collection.js.
function initProductDescription(page) {
  const about = page.querySelector('[data-pdp-about]');
  if (!about) return;
  const desc = about.querySelector('[data-pdp-description]');
  const toggle = about.querySelector('[data-pdp-about-toggle]');
  if (!desc || !toggle) return;

  // Clamp first, then reveal the toggle only if the text actually overflows.
  about.setAttribute('data-collapsed', '');
  if (desc.scrollHeight - desc.clientHeight > 2) {
    toggle.hidden = false;
  } else {
    about.removeAttribute('data-collapsed');
  }

  toggle.addEventListener('click', () => {
    const collapsed = about.toggleAttribute('data-collapsed');
    toggle.textContent = collapsed ? toggle.dataset.more : toggle.dataset.less;
    toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  });
}

function initProductPage() {
  const page = document.querySelector('[data-product-page]');
  if (!page) return;
  initGallery(page);
  initVariantPicker(page);
  initQuantitySelector(page);
  initPurchaseButtons(page);
  initProductDescription(page);
  initStickyBar(page);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductPage);
} else {
  initProductPage();
}
